import { useEffect } from 'react';
import { toast } from 'sonner';
import api from '../lib/api';

// Screen Capture & Export Protection (4 Nov 2026), Parts C2-C4: deterrents only, for
// users without export_allowed. Never described anywhere as "screenshot blocking".
export function useCaptureProtection(user) {
  const exportAllowed = !!user?.export_allowed;
  const blurEnabled = user?.capture_blur_enabled !== false;

  const logAttempt = (kind, page) => {
    api.post('/audit/capture-attempt', { kind, page }).catch(() => {});
  };

  // C2 — print body class + Ctrl/Cmd+P intercept
  useEffect(() => {
    if (exportAllowed) {
      document.body.classList.add('print-allowed');
    } else {
      document.body.classList.remove('print-allowed');
    }
    return () => document.body.classList.remove('print-allowed');
  }, [exportAllowed]);

  useEffect(() => {
    const onKeyDown = (e) => {
      const isPrint = (e.ctrlKey || e.metaKey) && (e.key === 'p' || e.key === 'P');
      if (isPrint && !exportAllowed) {
        e.preventDefault();
        toast.error('Printing Trade IQ is disabled. This attempt has been logged.');
        logAttempt('PRINT', window.location.pathname);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [exportAllowed]);

  // C3 — block copying outside inputs, disable context menu on tables, disable drag
  useEffect(() => {
    if (exportAllowed) return undefined;
    const isEditable = (el) => el?.closest?.('input, textarea, [contenteditable], [data-allow-copy]');
    const onCopy = (e) => { if (!isEditable(e.target)) e.preventDefault(); };
    const onCut = (e) => { if (!isEditable(e.target)) e.preventDefault(); };
    const onContextMenu = (e) => { if (e.target?.closest?.('table')) e.preventDefault(); };
    const onDragStart = (e) => {
      if (e.target?.closest?.('tr, img') && !isEditable(e.target)) e.preventDefault();
    };
    document.addEventListener('copy', onCopy);
    document.addEventListener('cut', onCut);
    document.addEventListener('contextmenu', onContextMenu);
    document.addEventListener('dragstart', onDragStart);
    return () => {
      document.removeEventListener('copy', onCopy);
      document.removeEventListener('cut', onCut);
      document.removeEventListener('contextmenu', onContextMenu);
      document.removeEventListener('dragstart', onDragStart);
    };
  }, [exportAllowed]);

  // C4 — PrintScreen deterrent + window-blur content blur
  useEffect(() => {
    if (exportAllowed) return undefined;
    const onKeyUp = (e) => {
      if (e.key === 'PrintScreen') {
        document.body.classList.add('capture-blurred');
        setTimeout(() => document.body.classList.remove('capture-blurred'), 3000);
        if (navigator.clipboard?.writeText) {
          navigator.clipboard.writeText('').catch(() => {});
        }
        logAttempt('PRINTSCREEN', window.location.pathname);
      }
    };
    window.addEventListener('keyup', onKeyUp);
    return () => window.removeEventListener('keyup', onKeyUp);
  }, [exportAllowed]);

  useEffect(() => {
    if (!blurEnabled) return undefined;
    const blur = () => document.body.classList.add('capture-blurred');
    const unblur = () => document.body.classList.remove('capture-blurred');
    const onVisibility = () => { if (document.visibilityState === 'hidden') blur(); else unblur(); };
    window.addEventListener('blur', blur);
    window.addEventListener('focus', unblur);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('blur', blur);
      window.removeEventListener('focus', unblur);
      document.removeEventListener('visibilitychange', onVisibility);
      unblur();
    };
  }, [blurEnabled]);
}

export default useCaptureProtection;
